import { FIELD_TYPES } from '../../../../shared/fields/field-types';
import { FieldWithDepartmentField } from '../../core/store/fields/field.model';
import {
    Asset,
    Character,
    Department,
    Disposition,
    ExcludedDisposition,
    Field,
    Project,
    ProjectCurrency,
    Set,
    SubDepartment,
    Transaction
} from '../../core/store/models';
import { buildPDropdownOptions } from '../../core/store/utils';
import { PDropdownOption } from '../../shared/interfaces/p-dropdown-options';
import { STANDARD_FIELDS } from './../../../../shared/fields/standard-fields';
import { Injectable } from "@angular/core";

export interface FormFieldSortObject {
    firstFields: string[];
    lastFields: string[];
}

@Injectable()
export class TransactionFormService {
    characterOptions: PDropdownOption[];
    dispositionOptions: PDropdownOption[];
    excludedDispositionOptions: PDropdownOption[] = [];
    locationOptions: PDropdownOption[];
    projectOptions: PDropdownOption[];
    projectCurrenciesOptions: PDropdownOption[];
    setOptions: PDropdownOption[];
    subDepartmentOptions: PDropdownOption[];
    asset: Asset;
    fields: FieldWithDepartmentField[];

    build(department: Department, subDepartments: SubDepartment[], dispositions: Disposition[], projects: Project[], fields: Field[], locations, excludedDispositions: ExcludedDisposition[], optional: { isSeries?, characters?: Character[], sets?: Set[], projectCurrencies?: ProjectCurrency[] } = {}) {
        this.locationOptions = buildPDropdownOptions(locations);
        this.dispositionOptions = buildPDropdownOptions(dispositions);
        if (excludedDispositions) {
            this.excludedDispositionOptions = buildPDropdownOptions(excludedDispositions);
        }
        this.projectOptions = buildPDropdownOptions(projects.map(project => ({ id: project.id, name: optional.isSeries ? 'Season ' + project.name : project.name })));
        if (optional.projectCurrencies) {
            this.projectCurrenciesOptions = buildPDropdownOptions(optional.projectCurrencies.map(pc => ({ id: pc.id, name: pc.accounting_code })))
        }
        this.subDepartmentOptions = buildPDropdownOptions(subDepartments.map(sd => ({ id: sd.id, name: sd.displayName() })));

        if (optional.characters) {
            this.characterOptions = buildPDropdownOptions(optional.characters.map(c => {
                const char = new Character(c);
                return { id: char.id, name: char.getNameWithCode() };
            }));
        }

        if (optional.sets) {
            this.setOptions = buildPDropdownOptions(optional.sets.map(s => {
                const set = new Set(s);
                return { id: set.id, name: set.getNameWithCode() };
            }));
        }

        this.fields = this.buildWithDeptFields(department, fields);
    }

    buildWithDeptFields(department: Department, fields: Field[]) {
        const deptFieldsWithFields = [];
        department.departmentFields?.map(departmentField => {
            const field = fields.find(f => departmentField.field_id === f.id);
            if (field) {
                deptFieldsWithFields.push({ departmentField, field });
            }
        });
        return deptFieldsWithFields;
    }

    getFieldFormObject(fieldArr: FieldWithDepartmentField[], transaction) {
        const formObject = {};
        fieldArr.forEach(field => {
            const initial = {
                value: field.field.getValueFromAsset(transaction),
                disabled: field.field.canonical_name === STANDARD_FIELDS.project.canonical_name || field.field.canonical_name === STANDARD_FIELDS.origin_currency.canonical_name
            };
            const validators = field.field.getValidators(field.departmentField.required);
            formObject[field.field.canonical_name] = [initial, validators];
        });
        return formObject;
    }

    orderFields(transaction: Transaction, sortObject: FormFieldSortObject, ignoreFields: Field[]) {
        const firstFields = [], restFields = [], lastFields = [], hiddenRequiredFields = [];
        this.fields.forEach((field) => {
            const fieldOptions = field.field.picklistOptions;
            const noFieldOptions = !fieldOptions || (fieldOptions && !fieldOptions.length);

            if (field.field.type === FIELD_TYPES.PICKLIST.type && noFieldOptions) {
                field.field = this.setupPicklistField(field.field);
            }
            const firstIndex = sortObject.firstFields.indexOf(field.field.canonical_name);
            const lastIndex = sortObject.lastFields.indexOf(field.field.canonical_name);

            if (!(ignoreFields && ignoreFields.find(ignore => field.field.canonical_name === ignore.canonical_name))) {
                if (firstIndex > -1) {
                    if (field.field.canonical_name === 'cost') {
                        field.field = new Field({ ...field.field, name: 'Origin Cost' });
                    }
                    firstFields[firstIndex] = field;
                } else if (lastIndex > -1) {
                    lastFields[lastIndex] = field;
                } else if (!field.field.getValueFromAsset(transaction) && field.departmentField.required && field.field.type !== FIELD_TYPES.IMAGE.type) {
                    restFields.push(field);
                } else if (field.departmentField.required) {
                    hiddenRequiredFields.push(field);
                }
            }
        });
        return {
            firstFields: firstFields.filter(e => e),
            restFields: restFields.filter(e => e).sort((f1, f2) => f1.field.sort_order - f2.field.sort_order),
            lastFields: lastFields.filter(e => e),
            hiddenRequiredFields: hiddenRequiredFields.filter(e => e)
        };
    }

    setupPicklistField(field: Field) {
        switch (field.canonical_name) {
            case STANDARD_FIELDS.disposition.canonical_name: {
                return new Field({ ...field, picklistOptions: this.dispositionOptions });
            }
            case STANDARD_FIELDS.project.canonical_name: {
                return new Field({ ...field, picklistOptions: this.projectOptions });
            }
            case STANDARD_FIELDS.set.canonical_name: {
                return new Field({ ...field, picklistOptions: this.setOptions });
            }
            case STANDARD_FIELDS.character.canonical_name: {
                return new Field({ ...field, picklistOptions: this.characterOptions });
            }
            case STANDARD_FIELDS.sub_department.canonical_name: {
                return new Field({ ...field, picklistOptions: this.subDepartmentOptions });
            }
            case STANDARD_FIELDS.location.canonical_name: {
                return new Field({ ...field, picklistOptions: this.locationOptions });
            }
            case STANDARD_FIELDS.origin_currency.canonical_name: {
                return new Field({ ...field, picklistOptions: this.projectCurrenciesOptions })
            }
            default: {
                return field;
            }
        }
    }

    transactionsTotalCost(transactions: Transaction[]): number {
        if (!transactions.length) return 0;

        return transactions.reduce((totalCost, curr) => {
            totalCost = totalCost + Number(curr.cost);
            return totalCost;
        }, 0);

    }
}
